import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment/moment";
import React from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IInvoice } from "../../../shared/interfaces/invoice/IInvoice";
import { lightPalette } from "../../../shared/mui/palettes";
import { RootState } from "../../../store/store";
import i18n from "../../../utils/localization/i18n";

interface IProps {
  control: Control<IInvoice>;
}

const InvoiceSchedule = ({ control }: IProps) => {
  const { t } = useTranslation();
  const { invoiceStatus } = useSelector((state: RootState) => state.enums);
  return (
    <>
      <Grid container spacing={2} sx={{ alignItems: "start", marginY: "1rem" }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <h3>{t("invoice.schedule")}:</h3>
          <Typography variant={"subtitle1"} color={lightPalette.text.gray}>
            {t("invoice.invoiceDetails")}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name={"invoiceNumber"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    error={!!error?.message}
                    helperText={t(error?.message as string)}
                    value={value ?? ""}
                    onChange={onChange}
                    label={t("invoice.invoiceNumber")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={"dueDate"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Box sx={{ width: "100%" }}>
                    <LocalizationProvider
                      dateAdapter={AdapterMoment}
                      adapterLocale={i18n.language}
                    >
                      <DemoContainer components={["DatePicker"]}>
                        <DatePicker
                          sx={{ width: "100%" }}
                          label={t("invoice.dueDate")}
                          value={moment(value) ?? ""}
                          onChange={onChange}
                          format="DD.MM.YYYY"
                          slotProps={{
                            textField: {
                              error: !!error,
                            },
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                    {!!error?.message?.length && (
                      <FormHelperText>{t(error?.message)}</FormHelperText>
                    )}
                  </Box>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name={"status"}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      className={!!error?.message?.length ? "Mui-error" : ""}
                      id="status-label"
                    >
                      {t("general.status")}
                    </InputLabel>
                    <Select
                      error={!!error?.message}
                      labelId="status-label"
                      id="status-select"
                      value={value ?? ""}
                      label={t("general.status")}
                      onChange={onChange}
                    >
                      {invoiceStatus.map((status) => {
                        return (
                          <MenuItem key={status} value={status}>
                            {t(`statuses.${status}`)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {!!error?.message?.length && (
                      <FormHelperText>{t(error?.message)}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
    </>
  );
};

export default InvoiceSchedule;
